import { useState } from "react";
import { toast } from "sonner";
import { Download, Facebook, Link2, Share2, Image as ImageIcon, Loader2 } from "lucide-react";
import { copyText } from "@/lib/ratings";

const API = process.env.REACT_APP_BACKEND_URL;
const btn = "rounded-full px-4 py-2 text-xs font-bold flex items-center gap-1.5 glass hover:scale-[1.03] transition-transform";

export default function ShareRatingCard({ player }) {
  const [v] = useState(() => Date.now());
  const [busy, setBusy] = useState(false);
  const pageUrl = `${API}/api/ratings/card/${player.user_id}`;
  const imgUrl = `${pageUrl}/image.png?v=${v}`;
  const file = `pointcontrol-rating-${player.player_code}.png`;

  const blob = async () => { const r = await fetch(imgUrl); if (!r.ok) throw new Error("card"); return r.blob(); };
  const download = async () => {
    setBusy(true);
    try {
      const url = URL.createObjectURL(await blob());
      const a = Object.assign(document.createElement("a"), { href: url, download: file });
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not download the card"); }
    setBusy(false);
  };
  const shareImage = async () => {
    try {
      const f = new File([await blob()], file, { type: "image/png" });
      if (navigator.canShare?.({ files: [f] })) {
        await navigator.share({ files: [f], title: `${player.name} on PointControl.Pro`, text: `My PointControl.Pro ratings — rate me here: ${pageUrl}` });
        return;
      }
    } catch (e) { if (e?.name === "AbortError") return; }
    await copyText(pageUrl);
    toast.success("Image sharing isn't supported here — card link copied instead");
  };
  const facebook = () => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`, "_blank", "noopener,width=640,height=560");

  return (
    <div className="rounded-2xl glow-border p-5" style={{ background: "var(--surface)" }} data-testid="share-rating-card">
      <div className="font-display font-bold flex items-center gap-2"><ImageIcon className="w-4 h-4 text-primary-c" /> Shareable Rating Card</div>
      <p className="text-sm mt-1" style={{ color: "var(--text-2)" }}>A 1080×1080 image of your ratings. Facebook posts show it as a preview with a "Rate me" link.</p>
      <div className="grid grid-cols-1 sm:grid-cols-[220px_1fr] gap-5 mt-4 items-start">
        <a href={imgUrl} target="_blank" rel="noreferrer" data-testid="rating-card-preview-link">
          <img src={imgUrl} alt="Rating card" className="w-full rounded-xl glow-border" data-testid="rating-card-preview" />
        </a>
        <div className="flex flex-wrap gap-2">
          <button data-testid="share-card-facebook-btn" onClick={facebook} className="btn-glow rounded-full px-4 py-2 text-xs font-bold flex items-center gap-1.5"><Facebook className="w-3.5 h-3.5" /> Share on Facebook</button>
          <button data-testid="download-card-btn" onClick={download} disabled={busy} className={btn}>{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5 text-primary-c" />} Download PNG</button>
          <button data-testid="share-card-image-btn" onClick={shareImage} className={btn}><Share2 className="w-3.5 h-3.5 text-primary-c" /> Share image</button>
          <button data-testid="copy-card-link-btn" onClick={async () => { await copyText(pageUrl); toast.success("Card link copied"); }} className={btn}><Link2 className="w-3.5 h-3.5 text-primary-c" /> Copy card link</button>
          <p className="text-[11px] w-full" style={{ color: "var(--text-2)" }}>The card link opens your active rating request (or your profile if you have no active link). Viewers still need to sign in and be verified before they can rate you.</p>
        </div>
      </div>
    </div>
  );
}
